import { Anchor, List, Text } from "@mantine/core";
import {
  LegalContactRow,
  LegalPageLayout,
  LegalSection,
} from "./LegalPageLayout";

// Condensed rendition of the canonical policy in the meta repo's
// docs/legal/07_Privacy_Policy.md (facts settled 2026-08-12: LivingSyncs,
// Inc., a Delaware corporation; P.O. Box 114, Splendora, TX 77372). Keep the
// two in sync. The §6 encryption-at-rest/MFA claims are prod go-live facts
// (prod RDS is created encrypted; Cognito MFA is ON in prod) — the remaining
// go-live checklist lives in docs/legal/00_README_Legal_Package.md.

export function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Privacy"
      titleAccent="Policy"
      metaDescription="How LivingSyncs collects, uses, and shares information from its website, and how resident health information is handled under HIPAA."
      lastUpdated="August 12, 2026"
    >
      <LegalSection heading="1. Who we are and what this policy covers">
        <Text c="dimmed">
          LivingSyncs (operated by LivingSyncs, Inc., a Delaware corporation,
          based in the greater Houston, Texas area) provides a management
          platform for group homes and assisted living facilities. This Privacy
          Policy describes how we collect, use, and share information when you
          visit our public website or contact us through it.
        </Text>
        <Text c="dimmed">
          This policy covers our marketing website only. If your organization
          uses the LivingSyncs application, the data your organization submits
          to it — including resident health information — is governed by the
          agreements between LivingSyncs and your organization (including a
          Business Associate Agreement and subscription agreement), not by this
          policy. See the &quot;Health information and HIPAA&quot; section
          below.
        </Text>
      </LegalSection>

      <LegalSection heading="2. Information we collect on this website">
        <Text c="dimmed">
          <strong>Information you give us.</strong> If you request a demo or
          contact us, we collect the details you submit: your name, email
          address, organization, phone number, and message. Please do not
          include any resident or patient health information in your message.
        </Text>
        <Text c="dimmed">
          <strong>Information collected automatically.</strong> We use error
          monitoring and performance telemetry (provided by Sentry) to keep the
          site reliable. This may capture your IP address, browser and device
          information, the pages you visited, and technical error context.
          Session diagnostics mask all typed text and block media by default.
        </Text>
        <Text c="dimmed">
          This website does not set advertising or analytics cookies, and we do
          not use third-party advertising trackers.
        </Text>
      </LegalSection>

      <LegalSection heading="3. How we use information">
        <List c="dimmed" spacing="xs">
          <List.Item>
            To respond to your demo request or inquiry and communicate with you
            about our services.
          </List.Item>
          <List.Item>
            To operate, secure, and improve this website, including diagnosing
            errors and performance problems.
          </List.Item>
          <List.Item>To comply with legal obligations.</List.Item>
        </List>
        <Text c="dimmed">
          We do not sell personal information, and we do not share it for
          third-party advertising.
        </Text>
      </LegalSection>

      <LegalSection heading="4. How information is shared">
        <Text c="dimmed">
          We share information only with service providers who help us run this
          website and respond to inquiries — currently Amazon Web Services
          (hosting and email delivery) and Sentry (error monitoring) — and when
          required by law. These providers process information on our behalf
          under their own contractual obligations.
        </Text>
      </LegalSection>

      <LegalSection heading="5. Retention">
        <Text c="dimmed">
          Demo and contact inquiries are delivered to us as email and are not
          stored in a separate marketing database; we keep that correspondence
          only as long as needed to handle your inquiry and follow up. Our
          application server also writes a log entry when a request is
          submitted, recording the email address and organization name, so that
          we can operate and secure the service; these logs are retained for up
          to 180 days and then deleted. Technical telemetry is retained per our
          error-monitoring provider's retention schedule and then deleted.
        </Text>
      </LegalSection>

      <LegalSection heading="6. Health information and HIPAA" id="hipaa">
        <Text c="dimmed">
          When a group home or assisted living organization uses the LivingSyncs
          application, LivingSyncs processes resident information — including
          protected health information (PHI) — on behalf of that organization as
          a HIPAA business associate, under a signed Business Associate
          Agreement. That information belongs to and is controlled by the
          organization, and this website policy does not apply to it.
        </Text>
        <Text c="dimmed">
          We protect application data with safeguards including encryption in
          transit and at rest, role-based access controls, multi-factor
          authentication, and audit logging.
        </Text>
        <Text c="dimmed">
          If you are a resident (or a resident's representative) and want to
          exercise your HIPAA rights — such as requesting access to records —
          please contact the group home or facility that provides your care. We
          support our customers in fulfilling those requests.
        </Text>
      </LegalSection>

      <LegalSection heading="7. Your privacy rights">
        <Text c="dimmed">
          Depending on your state of residence — including under the Texas Data
          Privacy and Security Act — you may have rights to request access to,
          correction of, or deletion of personal information we hold about you,
          or a portable copy of it. We do not sell personal data and do not use
          it for targeted advertising, so there is no sale or targeted
          advertising to opt out of. To make a request, email us at{" "}
          <Anchor href="mailto:privacy@livingsyncs.com">
            privacy@livingsyncs.com
          </Anchor>
          . We will verify and respond to requests as required by applicable
          law, and if we decline a request you may appeal by replying to our
          response with the word &quot;Appeal.&quot;
        </Text>
      </LegalSection>

      <LegalSection heading="8. Children">
        <Text c="dimmed">
          This website is intended for business audiences and is not directed to
          children under 13. We do not knowingly collect personal information
          from children through this website.
        </Text>
      </LegalSection>

      <LegalSection heading="9. Changes to this policy">
        <Text c="dimmed">
          We may update this policy from time to time. We will post the updated
          version on this page and revise the &quot;Last updated&quot; date
          above.
        </Text>
      </LegalSection>

      <LegalSection heading="10. Contact us">
        <LegalContactRow label="Questions about this policy or our privacy practices">
          <Anchor href="mailto:hello@livingsyncs.com">
            hello@livingsyncs.com
          </Anchor>{" "}
          or (832) 991-3017
        </LegalContactRow>
        <LegalContactRow label="Privacy requests">
          <Anchor href="mailto:privacy@livingsyncs.com">
            privacy@livingsyncs.com
          </Anchor>
        </LegalContactRow>
        <LegalContactRow label="Security reports">
          <Anchor href="mailto:security@livingsyncs.com">
            security@livingsyncs.com
          </Anchor>
        </LegalContactRow>
        <LegalContactRow label="Mailing address">
          LivingSyncs, Inc., P.O. Box 114, Splendora, TX 77372
        </LegalContactRow>
      </LegalSection>
    </LegalPageLayout>
  );
}
